// frontend/src/pages/admin/TokensPage.tsx
import { useCallback, useEffect, useState } from "react";
import {
  api,
  type AdminTokenCreated,
  type AdminTokenView,
  type AdminUserView,
} from "../../lib/api";

/** Format an ISO timestamp as a human-readable string, or "never" if null. */
function formatTime(s: string | null): string {
  if (!s) return "never";
  return new Date(s).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const KIND_CHIP: Record<string, string> = {
  static: "bg-amber-100 text-amber-800",
  oauth: "bg-indigo-100 text-indigo-800",
};

/** Collapsible setup card shown above the mint form. */
function ConnectorBanner() {
  return (
    <details className="mb-6 rounded-lg border border-slate-200 bg-white p-4 text-sm">
      <summary className="cursor-pointer font-medium text-slate-700">
        Connect from claude.ai or Claude Code
      </summary>
      <div className="mt-3 space-y-2 text-slate-600">
        <p>
          Add this MCP server in claude.ai → Settings → Connectors → Add custom.
          OAuth via your DataCamp Workspace login — no token paste required:
        </p>
        <pre className="overflow-x-auto rounded bg-slate-100 px-3 py-2 font-mono text-xs">
          {window.location.origin}/mcp/
        </pre>
        <p className="text-xs text-slate-500">
          Need a static token for local Claude Code? Mint one below on the
          user&apos;s behalf — the raw value is shown once.
        </p>
      </div>
    </details>
  );
}

/** Admin → Tokens. Mint on-behalf-of any user, list every token, revoke any. */
export function TokensPage() {
  const [tokens, setTokens] = useState<AdminTokenView[] | null>(null);
  const [users, setUsers] = useState<AdminUserView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reveal, setReveal] = useState<AdminTokenCreated | null>(null);

  const [targetEmail, setTargetEmail] = useState("");
  const [label, setLabel] = useState("");

  const load = useCallback(() => {
    setError(null);
    Promise.all([api.admin.tokens.list(), api.admin.listUsers()])
      .then(([t, u]) => {
        setTokens(t);
        setUsers(u);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  async function mint() {
    if (!targetEmail) return;
    setBusy("mint");
    setError(null);
    try {
      const created = await api.admin.tokens.mintOnBehalfOf(
        targetEmail,
        label || "untitled",
      );
      setReveal(created);
      setLabel("");
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function revoke(id: string, owner: string) {
    if (!confirm(`Revoke token for ${owner}?`)) return;
    setBusy(`rev:${id}`);
    setError(null);
    try {
      await api.admin.tokens.revoke(id);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="mb-2 text-2xl font-semibold">Tokens</h1>
      <p className="mb-4 text-sm text-slate-500">
        Static MCP tokens are admin-mint-only. Type any{" "}
        <code className="rounded bg-slate-100 px-1 text-xs">@datacamp.com</code>{" "}
        email — existing users autocomplete, but you can mint for someone who
        hasn't signed in yet (they'll be auto-provisioned on first login). The
        raw value is shown ONCE; only its SHA-256 hash is stored.
      </p>
      <p className="mb-4 text-sm text-amber-700">
        A token carries its owner&apos;s full permissions. If that user has
        connected Google, it can also read every spreadsheet they can reach —
        not just their artifacts&apos; declared ranges. Treat it as you would
        their password.
      </p>

      <ConnectorBanner />

      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold">Mint on behalf of</h2>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input
            type="email"
            list="known-emails"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            placeholder="user@datacamp.com"
            className="min-w-[260px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
          <datalist id="known-emails">
            {users?.map((u) => (
              <option key={u.email} value={u.email}>
                {u.role === "admin" ? "admin" : "user"}
                {u.is_active ? "" : " · deactivated"}
              </option>
            ))}
          </datalist>
          <input
            type="text"
            placeholder="Label (e.g., alice's laptop)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="min-w-[200px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={mint}
            disabled={!targetEmail.trim() || busy === "mint"}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {busy === "mint" ? "Minting…" : "Mint token"}
          </button>
        </div>
      </section>

      {reveal && (
        <div className="mb-6 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-900">
            New token for {reveal.owner_email}. Copy now — it will not be shown again.
          </p>
          <pre className="mt-2 overflow-x-auto rounded bg-white px-3 py-2 font-mono text-xs">
            {reveal.raw}
          </pre>
          <button
            type="button"
            onClick={() => setReveal(null)}
            className="mt-2 text-xs text-emerald-900 underline"
          >
            I copied it
          </button>
        </div>
      )}

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {tokens === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="text-slate-500">No tokens yet.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Last used</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2">{t.owner_email}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${KIND_CHIP[t.kind] || "bg-slate-100 text-slate-700"}`}
                    >
                      {t.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2">{t.label}</td>
                  <td className="px-3 py-2 text-slate-600">{formatTime(t.created_at)}</td>
                  <td className="px-3 py-2 text-slate-600">{formatTime(t.last_used_at)}</td>
                  <td className="px-3 py-2">
                    {t.enabled ? (
                      <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                        enabled
                      </span>
                    ) : (
                      <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        revoked
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.enabled && (
                      <button
                        type="button"
                        onClick={() => revoke(t.id, t.owner_email)}
                        disabled={busy === `rev:${t.id}`}
                        className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
